// pages/developer-page.tsx
//
// Public documentation for the Strongifier developer API, laid out in the
// style of Stripe's API reference (https://docs.stripe.com/api): a sticky
// section nav on the left and, per section, prose on the left with dark code
// panels on the right.
//
// Note: the app's global CSS sets `html { font-size: 10px }`, which shrinks
// all rem-based Tailwind text classes. This page therefore uses explicit
// pixel sizes (text-[15px] etc.) so it renders at normal documentation scale.

import React from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/page-layout";

const apiServerUrl =
  process.env.REACT_APP_API_SERVER_URL || "https://api.strongifier.com";

const authExample = `# Preferred: Authorization header
curl "${apiServerUrl}/api/v1/activities" \\
  -H "Authorization: Bearer stf_YOUR_API_KEY"

# Alternative: X-API-Key header
curl "${apiServerUrl}/api/v1/activities" \\
  -H "X-API-Key: stf_YOUR_API_KEY"`;

const listRequestExample = `curl "${apiServerUrl}/api/v1/activities?limit=10&offset=0" \\
  -H "Authorization: Bearer stf_YOUR_API_KEY"`;

const listResponseExample = `{
  "activities": [
    {
      "id": "fit-1c0e6b09-...",
      "activity_datetime": "2026-07-29T04:30:43Z",
      "local_activity_datetime": "2026-07-29T14:30:43+10:00",
      "local_timezone": "Australia/Brisbane",
      "duration_minutes": 30.42,
      "distance_km": 3.81,
      "activity_type": "Run",
      "average_heart_rate": 145.1,
      "strongifier_activity_name": "Morning Run",
      "hr_zone_stats": { "zones": [ ... ] },
      "fastest_20_minutes_after_10_minutes": { ... },
      "analysis_threshold_info": { ... },
      ...
    }
  ],
  "pagination": {
    "limit": 10,
    "offset": 0,
    "total": 42,
    "has_more": true
  }
}`;

const paginationExample = `# First page (10 most recent activities)
GET /api/v1/activities

# Next page
GET /api/v1/activities?offset=10

# Larger pages
GET /api/v1/activities?limit=100&offset=200`;

const errorExample = `{
  "message": "Invalid API key."
}`;

const activityObjectExample = `{
  "id": "fit-1c0e6b09-6b1d-4d3a-9a3e-2f6d9c1a4b7e",
  "activity_datetime": "2026-07-29T04:30:43Z",
  "local_activity_datetime": "2026-07-29T14:30:43+10:00",
  "local_timezone": "Australia/Brisbane",
  "duration_minutes": 30.42,
  "distance_km": 3.81,
  "activity_type": "Run",
  "average_heart_rate": 145.1,
  "strongifier_activity_name": "Morning Run",
  "strava_activity_id": null,
  "strava_activity_name": null,
  "completed_activity_id": null,
  "is_threshold_test": false,
  "is_max_effort": false,
  "fastest_60_minutes": null,
  "fastest_30_minutes_after_15_minutes": null,
  "fastest_20_minutes_after_10_minutes": {
    "activity_id": "fit-1c0e6b09-6b1d-4d3a-9a3e-2f6d9c1a4b7e",
    "activity_name": "Morning Run",
    "activity_date": "2026-07-29",
    "segment_duration": 20.0,
    "start_after_minutes": 10.0,
    "start_time": "04:40:43",
    "distance": 2.27,
    "average_pace": 8.81,
    "average_heart_rate": 148.45
  },
  "hr_zone_stats": {
    "zones": [
      {
        "zone_number": 0,
        "hr_range": "<= 141 bpm",
        "time_in_seconds": 780,
        "distance_km": 1.52
      },
      {
        "zone_number": 1,
        "hr_range": "142-155 bpm",
        "time_in_seconds": 645,
        "distance_km": 1.48
      },
      ...
    ]
  },
  "analysis_is_long_activity": false,
  "analysis_threshold_info": {
    "value": {
      "threshold_pace": 8.81,
      "threshold_heart_rate": 148.45
    },
    "explanation": "Threshold values were determined using the fastest 20-minute segment (pace: 8.81 min/km, HR: 148 bpm) from this activity.",
    "calculation_type": "30_to_45_minute_effort"
  },
  "analysis_running_threshold_information": {
    "threshold_history": [
      {
        "activity_id": "fit-a91c73d2-...",
        "activity_date": "2026-07-12",
        "estimated_pace": 5.42,
        "estimated_heart_rate": 172.0
      }
    ],
    "threshold_pace": 5.42,
    "threshold_heart_rate": 172.0,
    "threshold_pace_activity_id": "fit-a91c73d2-...",
    "threshold_heart_rate_activity_id": "fit-a91c73d2-..."
  },
  "analysis_new_run_threshold_detected": false,
  "analysis_is_legs_dominant_endurance_exercise": true,
  "analysis_local_week_number": 134,
  "analysis_long_leg_dominant_effort_info": {
    "value": {
      "weeks": [
        {
          "week_number": 134,
          "activities": [
            {
              "activity_strongifier_id": "fit-1c0e6b09-...",
              "activity_name": "Morning Run",
              "duration_minutes": 30.42,
              "local_datetime": "2026-07-29T14:30:43+10:00"
            }
          ],
          "long_activity": null
        }
      ],
      "is_long_activity": false
    },
    "explanation": "Not enough historical activity data to determine if this is a long-duration effort."
  }
}`;

/** Dark code panel with a Stripe-style title bar. */
const CodePanel: React.FC<{ title: string; children: string }> = ({
  title,
  children,
}) => (
  <div className="rounded-lg overflow-hidden border border-[#242e42] shadow-md mb-6">
    <div className="bg-[#242e42] px-4 py-2 text-[13px] font-semibold uppercase tracking-wider text-[#9aa5b8]">
      {title}
    </div>
    <pre className="bg-[#1a2130] p-4 overflow-x-auto text-[14px] leading-[22px] text-[#e8ecf4]">
      {/* The color class must live on <code>: general.css's `code {color}` element rule would otherwise override the inherited panel color. */}
      <code className="text-[#e8ecf4]">{children}</code>
    </pre>
  </div>
);

/** Inline code chip for prose. */
const InlineCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="bg-[#242e42] text-[#dfe5f0] rounded px-1.5 py-0.5 font-mono text-[14px]">
    {children}
  </code>
);

/** A Stripe-style parameter/row definition. */
const ParamRow: React.FC<{
  name: string;
  type: string;
  meta?: string;
  children: React.ReactNode;
}> = ({ name, type, meta, children }) => (
  <div className="py-4 border-b border-[#1f2839] last:border-b-0">
    <div className="mb-1">
      <span className="font-mono text-[15px] font-bold text-[#e8ecf4]">
        {name}
      </span>
      <span className="ml-2 text-[13px] text-[#9aa5b8]">{type}</span>
      {meta && (
        <span className="ml-2 text-[13px] text-[#9aa5b8] italic">{meta}</span>
      )}
    </div>
    <div className="text-[15px] leading-[24px] text-[#aab4c8]">{children}</div>
  </div>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-[26px] leading-[34px] font-bold text-[#e8ecf4] mb-4">
    {children}
  </h2>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-[14px] font-semibold uppercase tracking-wider text-[#9aa5b8] border-b border-[#232c3e] pb-2 mb-2 mt-8">
    {children}
  </h3>
);

/** Two-column documentation section: prose left, code panels right. */
const DocSection: React.FC<{
  id: string;
  left: React.ReactNode;
  right?: React.ReactNode;
}> = ({ id, left, right }) => (
  <section
    id={id}
    className="grid grid-cols-1 xl:grid-cols-2 gap-x-12 gap-y-8 py-12 border-b border-[#232c3e]"
  >
    <div className="min-w-0">{left}</div>
    <div className="min-w-0 xl:pt-1">{right}</div>
  </section>
);

const navLinkClass =
  "block py-1.5 text-[15px] text-[#aab4c8] hover:text-[#b3c1ff]";

/** In-page anchor link, e.g. to #the-activity-object. */
const AnchorLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <a href={to} className="text-[#8fa3ff] font-medium hover:underline">
    {children}
  </a>
);

export const DeveloperPage: React.FC = () => {
  return (
    <PageLayout>
      <div className="w-full text-[16px] leading-[26px] text-[#aab4c8]">
        <div className="flex">
          {/* Section navigation */}
          <aside className="hidden lg:block w-[240px] shrink-0 border-r border-[#232c3e]">
            <nav className="sticky top-0 px-6 py-12">
              <div className="text-[13px] font-semibold uppercase tracking-wider text-[#9aa5b8] mb-2">
                Getting Started
              </div>
              <a href="#introduction" className={navLinkClass}>
                Introduction
              </a>
              <a href="#authentication" className={navLinkClass}>
                Authentication
              </a>
              <a href="#pagination" className={navLinkClass}>
                Pagination
              </a>
              <a href="#errors" className={navLinkClass}>
                Errors
              </a>
              <div className="text-[13px] font-semibold uppercase tracking-wider text-[#9aa5b8] mb-2 mt-8">
                Activities
              </div>
              <a href="#the-activity-object" className={navLinkClass}>
                The Activity object
              </a>
              <a href="#list-activities" className={navLinkClass}>
                List activities
              </a>
            </nav>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0 px-6 lg:px-12 pb-16">
            {/* Introduction */}
            <DocSection
              id="introduction"
              left={
                <>
                  <h1 className="text-[30px] leading-[38px] font-bold text-[#e8ecf4] mb-4 pt-2">
                    Strongifier API
                  </h1>
                  <p className="mb-4">
                    The Strongifier API lets your programs and websites read a
                    user's Strongifier data, such as their workout history. It
                    is organized around REST: it has predictable
                    resource-oriented URLs, returns JSON responses, and uses
                    standard HTTP response codes.
                  </p>
                  <p>
                    Access is authenticated with API keys. Any Strongifier user
                    can create one on their{" "}
                    <Link
                      to="/api-keys"
                      className="text-[#8fa3ff] font-medium hover:underline"
                    >
                      API Keys
                    </Link>{" "}
                    page.
                  </p>
                </>
              }
              right={<CodePanel title="Base URL">{apiServerUrl}</CodePanel>}
            />

            {/* Authentication */}
            <DocSection
              id="authentication"
              left={
                <>
                  <SectionHeading>Authentication</SectionHeading>
                  <p className="mb-4">
                    The Strongifier API uses API keys to authenticate requests.
                    Keys start with <InlineCode>stf_</InlineCode> and are shown
                    exactly once, when created. Include your key with every
                    request, either as a bearer token in the{" "}
                    <InlineCode>Authorization</InlineCode> header or in an{" "}
                    <InlineCode>X-API-Key</InlineCode> header.
                  </p>
                  <p className="mb-4">
                    Your API keys carry read access to the data of the user who
                    created them, so keep them secure. Do not embed keys in web
                    pages or mobile apps where users could extract them; make
                    API calls from your server instead.
                  </p>
                  <p>
                    Keys can be revoked at any time from the{" "}
                    <Link
                      to="/api-keys"
                      className="text-[#8fa3ff] font-medium hover:underline"
                    >
                      API Keys
                    </Link>{" "}
                    page; revocation is immediate. Requests without a valid key
                    receive <InlineCode>401 Unauthorized</InlineCode>.
                  </p>
                </>
              }
              right={
                <CodePanel title="Authenticated Request">
                  {authExample}
                </CodePanel>
              }
            />

            {/* Pagination */}
            <DocSection
              id="pagination"
              left={
                <>
                  <SectionHeading>Pagination</SectionHeading>
                  <p className="mb-4">
                    List endpoints return the most recent items first and are
                    paginated with <InlineCode>limit</InlineCode> and{" "}
                    <InlineCode>offset</InlineCode> query parameters. By
                    default they return 10 items;{" "}
                    <InlineCode>limit</InlineCode> can be raised to 100.
                  </p>
                  <p>
                    Every list response includes a{" "}
                    <InlineCode>pagination</InlineCode> object with the total
                    number of items. While{" "}
                    <InlineCode>has_more</InlineCode> is{" "}
                    <InlineCode>true</InlineCode>, fetch the next page with{" "}
                    <InlineCode>offset = offset + limit</InlineCode>.
                  </p>
                </>
              }
              right={
                <CodePanel title="Paginated Requests">
                  {paginationExample}
                </CodePanel>
              }
            />

            {/* Errors */}
            <DocSection
              id="errors"
              left={
                <>
                  <SectionHeading>Errors</SectionHeading>
                  <p>
                    Strongifier uses conventional HTTP response codes to
                    indicate the success or failure of an API request. Error
                    responses include a JSON body with a human-readable{" "}
                    <InlineCode>message</InlineCode>.
                  </p>
                  <SubHeading>HTTP Status Codes</SubHeading>
                  <ParamRow name="200" type="OK">
                    Everything worked as expected.
                  </ParamRow>
                  <ParamRow name="401" type="Unauthorized">
                    The API key was missing, invalid, or has been revoked.
                  </ParamRow>
                  <ParamRow name="422" type="Unprocessable Entity">
                    A query parameter was invalid — for example,{" "}
                    <InlineCode>limit</InlineCode> greater than 100.
                  </ParamRow>
                </>
              }
              right={<CodePanel title="Error Response">{errorExample}</CodePanel>}
            />

            {/* The Activity object */}
            <DocSection
              id="the-activity-object"
              left={
                <>
                  <SectionHeading>The Activity object</SectionHeading>
                  <p className="mb-4">
                    An Activity represents one completed workout, together with
                    the analysis Strongifier has computed for it. Timestamps
                    are ISO 8601; paces are minutes per kilometer; distances
                    are kilometers; heart rates are beats per minute. Analysis
                    fields are <InlineCode>null</InlineCode> when the workout
                    doesn't contain enough data to compute them.
                  </p>
                  <p className="mb-4">
                    Activities come from two sources — imported from Strava or
                    uploaded as a FIT file — and a few fields (
                    <InlineCode>id</InlineCode>,{" "}
                    <InlineCode>activity_type</InlineCode>,{" "}
                    <InlineCode>hr_zone_stats</InlineCode>, and the run
                    segment <InlineCode>start_time</InlineCode>) differ in
                    format between the two; the differences are called out on
                    each field below. The example on the right shows an
                    uploaded FIT file.
                  </p>

                  <SubHeading>Attributes</SubHeading>
                  <ParamRow name="id" type="string">
                    Unique identifier for the activity:{" "}
                    <InlineCode>fit-&lt;uuid&gt;</InlineCode> for uploaded FIT
                    files (e.g. <InlineCode>fit-1c0e6b09-...</InlineCode>), or{" "}
                    <InlineCode>STRAVA-&lt;number&gt;</InlineCode> for workouts
                    imported from Strava (e.g.{" "}
                    <InlineCode>STRAVA-11223344556</InlineCode>).
                  </ParamRow>
                  <ParamRow name="activity_datetime" type="timestamp">
                    Start of the workout, in UTC.
                  </ParamRow>
                  <ParamRow
                    name="local_activity_datetime"
                    type="timestamp"
                    meta="nullable"
                  >
                    Start of the workout in the athlete's local timezone.
                  </ParamRow>
                  <ParamRow name="local_timezone" type="string" meta="nullable">
                    IANA timezone name the workout was recorded in, e.g.{" "}
                    <InlineCode>Australia/Brisbane</InlineCode>.
                  </ParamRow>
                  <ParamRow name="duration_minutes" type="number">
                    Total duration of the workout, in minutes.
                  </ParamRow>
                  <ParamRow name="distance_km" type="number">
                    Total distance covered, in kilometers.
                  </ParamRow>
                  <ParamRow name="activity_type" type="string">
                    The sport, as a Strava-style CamelCase activity type, e.g.{" "}
                    <InlineCode>Run</InlineCode>,{" "}
                    <InlineCode>Ride</InlineCode>,{" "}
                    <InlineCode>Swim</InlineCode>,{" "}
                    <InlineCode>VirtualRide</InlineCode>,{" "}
                    <InlineCode>WeightTraining</InlineCode>. Uploaded FIT
                    files have their Garmin sport/sub-sport normalized to the
                    same vocabulary (e.g. <InlineCode>running</InlineCode>{" "}
                    becomes <InlineCode>Run</InlineCode>). Workouts imported
                    before this normalization may still carry Garmin's
                    lowercase sport name, so compare values
                    case-insensitively.
                  </ParamRow>
                  <ParamRow
                    name="average_heart_rate"
                    type="number"
                    meta="nullable"
                  >
                    Average heart rate over the whole workout, in bpm.{" "}
                    <InlineCode>null</InlineCode> when the workout has no
                    heart-rate data.
                  </ParamRow>
                  <ParamRow
                    name="strongifier_activity_name"
                    type="string"
                    meta="nullable"
                  >
                    The activity's display name in Strongifier.
                  </ParamRow>
                  <ParamRow
                    name="strava_activity_id"
                    type="integer"
                    meta="nullable"
                  >
                    For activities imported from Strava, the Strava activity
                    id. <InlineCode>null</InlineCode> for uploaded FIT files.
                  </ParamRow>
                  <ParamRow
                    name="strava_activity_name"
                    type="string"
                    meta="nullable"
                  >
                    For activities imported from Strava, the Strava activity
                    name.
                  </ParamRow>
                  <ParamRow
                    name="completed_activity_id"
                    type="string"
                    meta="nullable"
                  >
                    Reserved for linking a completed activity to a planned
                    workout. Currently always <InlineCode>null</InlineCode>.
                  </ParamRow>
                  <ParamRow name="is_threshold_test" type="boolean">
                    Whether the athlete marked this workout as a threshold
                    test.
                  </ParamRow>
                  <ParamRow name="is_max_effort" type="boolean">
                    Whether the athlete marked this workout as a maximal
                    effort.
                  </ParamRow>
                  <ParamRow
                    name="fastest_60_minutes"
                    type="run segment"
                    meta="nullable"
                  >
                    The fastest 60-minute stretch of the workout — a Run
                    segment object (see below).{" "}
                    <InlineCode>null</InlineCode> for workouts shorter than 60
                    minutes or without distance data.
                  </ParamRow>
                  <ParamRow
                    name="fastest_30_minutes_after_15_minutes"
                    type="run segment"
                    meta="nullable"
                  >
                    The fastest 30-minute stretch that starts at least 15
                    minutes into the workout (i.e. after a warm-up).
                  </ParamRow>
                  <ParamRow
                    name="fastest_20_minutes_after_10_minutes"
                    type="run segment"
                    meta="nullable"
                  >
                    The fastest 20-minute stretch that starts at least 10
                    minutes into the workout.
                  </ParamRow>
                  <ParamRow name="hr_zone_stats" type="object" meta="nullable">
                    Time and distance per heart-rate zone; contains a{" "}
                    <InlineCode>zones</InlineCode> array of zone-stats objects
                    (see below). Which zones appear depends on the workout's
                    source — see the Zone stats object. When no threshold
                    heart rate was known at the time of the workout, uploaded
                    FIT files have an empty <InlineCode>zones</InlineCode>{" "}
                    array, while Strava imports have{" "}
                    <InlineCode>hr_zone_stats: null</InlineCode>.
                  </ParamRow>
                  <ParamRow name="analysis_is_long_activity" type="boolean">
                    Whether Strongifier classified this as the athlete's long
                    workout for the week.
                  </ParamRow>
                  <ParamRow
                    name="analysis_threshold_info"
                    type="object"
                    meta="nullable"
                  >
                    The threshold estimate derived from{" "}
                    <em>this workout alone</em>: a{" "}
                    <InlineCode>value</InlineCode> object with{" "}
                    <InlineCode>threshold_pace</InlineCode> and{" "}
                    <InlineCode>threshold_heart_rate</InlineCode>, a
                    human-readable <InlineCode>explanation</InlineCode>, and a{" "}
                    <InlineCode>calculation_type</InlineCode> of{" "}
                    <InlineCode>long_effort</InlineCode>,{" "}
                    <InlineCode>45_to_60_minute_effort</InlineCode>,{" "}
                    <InlineCode>30_to_45_minute_effort</InlineCode>,{" "}
                    <InlineCode>sub_thirty_minute_effort</InlineCode>, or{" "}
                    <InlineCode>unknown_effort</InlineCode>.
                  </ParamRow>
                  <ParamRow
                    name="analysis_running_threshold_information"
                    type="object"
                    meta="nullable"
                  >
                    The athlete's <em>running</em> threshold state as of this
                    workout, carried forward from previous workouts:{" "}
                    <InlineCode>threshold_pace</InlineCode>,{" "}
                    <InlineCode>threshold_heart_rate</InlineCode>, the ids of
                    the activities those came from, and a{" "}
                    <InlineCode>threshold_history</InlineCode> array of{" "}
                    {"{"}
                    <InlineCode>activity_id</InlineCode>,{" "}
                    <InlineCode>activity_date</InlineCode>,{" "}
                    <InlineCode>estimated_pace</InlineCode>,{" "}
                    <InlineCode>estimated_heart_rate</InlineCode>
                    {"}"} entries.
                  </ParamRow>
                  <ParamRow
                    name="analysis_new_run_threshold_detected"
                    type="boolean"
                  >
                    Whether this workout produced a new (better) threshold
                    estimate than the athlete's previous one.
                  </ParamRow>
                  <ParamRow
                    name="analysis_is_legs_dominant_endurance_exercise"
                    type="boolean"
                  >
                    Whether the sport primarily loads the legs (running,
                    hiking, etc.) — used for long-run tracking.
                  </ParamRow>
                  <ParamRow
                    name="analysis_local_week_number"
                    type="integer"
                    meta="nullable"
                  >
                    Strongifier's internal week index (Monday-start weeks,
                    counted from the first week of 2024, in the athlete's
                    timezone).
                  </ParamRow>
                  <ParamRow
                    name="analysis_long_leg_dominant_effort_info"
                    type="object"
                    meta="nullable"
                  >
                    Recent weekly long-run history used to classify this
                    workout: a <InlineCode>value.weeks</InlineCode> array
                    (each with <InlineCode>week_number</InlineCode>, its{" "}
                    <InlineCode>activities</InlineCode>, and that week's{" "}
                    <InlineCode>long_activity</InlineCode>) plus an{" "}
                    <InlineCode>explanation</InlineCode>.
                  </ParamRow>

                  <SubHeading>The Run segment object</SubHeading>
                  <p className="mb-2">
                    Used by the <InlineCode>fastest_*</InlineCode> attributes.
                  </p>
                  <ParamRow name="activity_id" type="string">
                    Id of the activity the segment belongs to.
                  </ParamRow>
                  <ParamRow name="activity_name" type="string">
                    Name of the activity the segment belongs to.
                  </ParamRow>
                  <ParamRow name="activity_date" type="string">
                    Date of the segment, <InlineCode>YYYY-MM-DD</InlineCode>.
                  </ParamRow>
                  <ParamRow name="segment_duration" type="number">
                    Length of the segment window, in minutes (20, 30, or 60).
                  </ParamRow>
                  <ParamRow name="start_after_minutes" type="number">
                    The earliest point the segment was allowed to start, in
                    minutes into the workout (0, 10, or 15, matching the{" "}
                    <InlineCode>fastest_*</InlineCode> attribute the segment
                    belongs to). The segment itself may start later than this.
                  </ParamRow>
                  <ParamRow name="start_time" type="string">
                    When the segment starts. The format depends on the
                    workout's source: for uploaded FIT files, the wall-clock
                    start time of the segment in UTC,{" "}
                    <InlineCode>HH:MM:SS</InlineCode> (e.g.{" "}
                    <InlineCode>04:40:43</InlineCode>); for Strava imports,
                    the elapsed time from the start of the workout,{" "}
                    <InlineCode>H:MM:SS</InlineCode> with no zero-padding on
                    hours (e.g. <InlineCode>0:10:00</InlineCode>).
                  </ParamRow>
                  <ParamRow name="distance" type="number">
                    Distance covered during the segment, in kilometers.
                  </ParamRow>
                  <ParamRow name="average_pace" type="number">
                    Average pace over the segment, in minutes per kilometer.
                  </ParamRow>
                  <ParamRow
                    name="average_heart_rate"
                    type="number"
                    meta="nullable"
                  >
                    Average heart rate over the segment, in bpm.
                  </ParamRow>

                  <SubHeading>The Zone stats object</SubHeading>
                  <p className="mb-2">
                    The elements of <InlineCode>hr_zone_stats.zones</InlineCode>.
                  </p>
                  <ParamRow name="zone_number" type="integer">
                    Heart-rate zone number. Uploaded FIT files include zones
                    0 (below Zone 1) through 5; Strava imports omit zone 0
                    and include zones 1 through 5.
                  </ParamRow>
                  <ParamRow name="hr_range" type="string">
                    Human-readable bpm range for the zone. For uploaded FIT
                    files the forms are{" "}
                    <InlineCode>&lt;= 141 bpm</InlineCode>,{" "}
                    <InlineCode>142-155 bpm</InlineCode>, and{" "}
                    <InlineCode>&gt; 175 bpm</InlineCode> for the top zone.
                    For Strava imports the forms are{" "}
                    <InlineCode>118-141 bpm</InlineCode> (the lower bound
                    repeats the previous zone's upper bound) and{" "}
                    <InlineCode>175+ bpm</InlineCode> for the top zone. Treat
                    this as display text only — use{" "}
                    <InlineCode>zone_number</InlineCode> for logic.
                  </ParamRow>
                  <ParamRow name="time_in_seconds" type="integer">
                    Time spent in the zone, in seconds.
                  </ParamRow>
                  <ParamRow name="distance_km" type="number">
                    Distance covered while in the zone, in kilometers.
                  </ParamRow>
                </>
              }
              right={
                <CodePanel title="The Activity object">
                  {activityObjectExample}
                </CodePanel>
              }
            />

            {/* List activities */}
            <DocSection
              id="list-activities"
              left={
                <>
                  <SectionHeading>List activities</SectionHeading>
                  <div className="mb-4">
                    <span className="inline-block bg-[#5661f0] text-white text-[13px] font-bold rounded px-2 py-0.5 mr-2 align-middle">
                      GET
                    </span>
                    <span className="font-mono text-[15px] text-[#e8ecf4] align-middle">
                      /api/v1/activities
                    </span>
                  </div>
                  <p className="mb-4">
                    Returns the user's completed workouts, sorted most recent
                    first. Each item is an{" "}
                    <AnchorLink to="#the-activity-object">
                      Activity object
                    </AnchorLink>{" "}
                    with full details — duration, distance, average heart
                    rate, heart-rate-zone stats, fastest-segment and threshold
                    analysis, and more.
                  </p>
                  <SubHeading>Query Parameters</SubHeading>
                  <ParamRow name="limit" type="integer" meta="optional, default 10">
                    Number of activities to return, between 1 and 100.
                  </ParamRow>
                  <ParamRow name="offset" type="integer" meta="optional, default 0">
                    Number of activities to skip, counting from the most
                    recent. Use <InlineCode>offset=10</InlineCode>,{" "}
                    <InlineCode>offset=20</InlineCode>, ... to page back
                    through older activities.
                  </ParamRow>
                  <SubHeading>Returns</SubHeading>
                  <p>
                    An <InlineCode>activities</InlineCode> array of{" "}
                    <AnchorLink to="#the-activity-object">
                      Activity objects
                    </AnchorLink>{" "}
                    sorted most recent first, and a{" "}
                    <InlineCode>pagination</InlineCode> object (
                    <InlineCode>limit</InlineCode>,{" "}
                    <InlineCode>offset</InlineCode>,{" "}
                    <InlineCode>total</InlineCode>,{" "}
                    <InlineCode>has_more</InlineCode>).
                  </p>
                </>
              }
              right={
                <>
                  <CodePanel title="Request">{listRequestExample}</CodePanel>
                  <CodePanel title="Response">{listResponseExample}</CodePanel>
                </>
              }
            />
          </main>
        </div>
      </div>
    </PageLayout>
  );
};
